// Appointments and SMS reminders (docs/plans/phase2-appointments.md §3.2). Internal UUIDs
// and days only in URLs.
import { queryOptions } from '@tanstack/react-query';
import {
  adminScheduleListSchema,
  appointmentDaySchema,
  appointmentViewSchema,
  patientAppointmentsSchema,
  scheduleListSchema,
  scheduleViewSchema,
  slotListSchema,
  type BookAppointmentBody,
  type ReminderDecision,
  type ScheduleBody,
} from '@wodi/shared';

import { api } from './client.ts';

export const appointmentKeys = {
  all: ['appointments'] as const,
  day: (day: string, scheduleId: string | undefined) =>
    ['appointments', 'day', day, scheduleId ?? 'all'] as const,
  patient: (patientId: string) => ['appointments', 'patient', patientId] as const,
  slots: (scheduleId: string, day: string) => ['appointments', 'slots', scheduleId, day] as const,
  schedules: ['appointment-schedules'] as const,
  adminSchedules: ['appointment-schedules', 'admin'] as const,
};

export const schedulesQuery = queryOptions({
  queryKey: appointmentKeys.schedules,
  queryFn: () => api('GET', '/appointment-schedules', { schema: scheduleListSchema }),
  staleTime: 60_000,
});

export const adminSchedulesQuery = queryOptions({
  queryKey: appointmentKeys.adminSchedules,
  queryFn: () => api('GET', '/admin/appointment-schedules', { schema: adminScheduleListSchema }),
});

export const slotsQuery = (scheduleId: string, day: string) =>
  queryOptions({
    queryKey: appointmentKeys.slots(scheduleId, day),
    queryFn: () =>
      api('GET', `/appointment-schedules/${scheduleId}/slots?day=${day}`, {
        schema: slotListSchema,
      }),
  });

export const appointmentDayQuery = (day: string, scheduleId: string | undefined) =>
  queryOptions({
    queryKey: appointmentKeys.day(day, scheduleId),
    queryFn: () =>
      api(
        'GET',
        `/appointments?day=${day}${scheduleId === undefined ? '' : `&scheduleId=${scheduleId}`}`,
        { schema: appointmentDaySchema },
      ),
  });

export const patientAppointmentsQuery = (patientId: string) =>
  queryOptions({
    queryKey: appointmentKeys.patient(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/appointments`, { schema: patientAppointmentsSchema }),
  });

export function bookAppointment(body: BookAppointmentBody) {
  return api('POST', '/appointments', { body, schema: appointmentViewSchema });
}

export function rescheduleAppointment(
  id: string,
  body: { scheduleId: string; startsAt: string; reason: string },
) {
  return api('POST', `/appointments/${id}/reschedule`, { body, schema: appointmentViewSchema });
}

export function cancelAppointment(id: string, reason: string) {
  return api('POST', `/appointments/${id}/cancel`, {
    body: { reason },
    schema: appointmentViewSchema,
  });
}

export function markNoShow(id: string, reason?: string) {
  return api('POST', `/appointments/${id}/no-show`, {
    body: reason === undefined ? {} : { reason },
    schema: appointmentViewSchema,
  });
}

export function recordReminderConsent(
  patientId: string,
  body: { decision: ReminderDecision; reason?: string },
) {
  return api('POST', `/patients/${patientId}/reminder-consent`, {
    body,
    schema: patientAppointmentsSchema,
  });
}

export function createSchedule(body: ScheduleBody) {
  return api('POST', '/admin/appointment-schedules', { body, schema: scheduleViewSchema });
}

export function updateSchedule(id: string, body: ScheduleBody) {
  return api('PUT', `/admin/appointment-schedules/${id}`, { body, schema: scheduleViewSchema });
}
